import { useMemo, useState } from 'react';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { ORG_KINDS } from '../lib/format';
import { Badge, Button, Card, Empty, PageHeader } from '../ui/kit';
import { RecordDrawer, type FieldSpec } from '../ui/RecordDrawer';

type Org = Record<string, unknown> & { id: string; name: string; short_code: string | null; kind: string; aliases: string[]; website: string | null; notes: string | null; deleted_at: string | null };
type Contact = Record<string, unknown> & {
  id: string; org_id: string | null; full_name: string; role_title: string | null; email: string | null; phone: string | null; whatsapp: string | null;
  notify_email: boolean; notify_whatsapp: boolean; deleted_at: string | null;
};
type SiteContact = Record<string, unknown> & { id: string; contact_id: string; site_role: string; notes: string | null; deleted_at: string | null };

const NONE = '00000000-0000-0000-0000-000000000000';
export const SITE_ROLES = [
  { value: 'client_finance', label: 'Client finance' },
  { value: 'client_team', label: 'Client team' },
  { value: 'architect_of_record', label: 'Architect of record' },
  { value: 'architect', label: 'Architect' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'contractor_pm', label: 'Contractor PM' },
  { value: 'contractor_site', label: 'Contractor site team' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'other', label: 'Other' },
];

export default function Orgs() {
  const { can, siteId, site } = useApp();
  const [tab, setTab] = useState<'orgs' | 'contacts' | 'site'>('orgs');
  const [q, setQ] = useState('');
  const [openOrg, setOpenOrg] = useState<Org | null | 'new'>(null);
  const [openContact, setOpenContact] = useState<Contact | null | 'new'>(null);
  const [openSite, setOpenSite] = useState<SiteContact | null | 'new'>(null);

  const orgs = useRows<Org>(() => supabase.from('organisations').select('*').order('name'), [], ['organisations']);
  const contacts = useRows<Contact>(() => supabase.from('contacts').select('*').order('full_name'), [], ['contacts']);
  const siteContacts = useRows<SiteContact>(
    () => supabase.from('site_contacts').select('*').eq('site_id', siteId ?? NONE).order('site_role'),
    [siteId],
    ['site_contacts'],
  );

  const orgName = (id: string | null) => orgs.rows.find((o) => o.id === id)?.name ?? '—';
  const editable = can('orgs', 4);
  const match = (s: string) => !q || s.toLowerCase().includes(q.toLowerCase());

  const orgFields: FieldSpec[] = useMemo(
    () => [
      { key: 'name', label: 'Name', required: true },
      { key: 'short_code', label: 'Short code' },
      { key: 'kind', label: 'Type', type: 'select', required: true, options: ORG_KINDS.map((k) => ({ value: k, label: k[0]!.toUpperCase() + k.slice(1) })) },
      { key: 'aliases', label: 'Other names / spellings', type: 'list', hint: 'Comma separated — used to match old Smartsheet entries' },
      { key: 'website', label: 'Website', type: 'url' },
      { key: 'notes', label: 'Notes / role on the project', type: 'textarea' },
    ],
    [],
  );
  const contactFields: FieldSpec[] = useMemo(
    () => [
      { key: 'full_name', label: 'Name', required: true },
      { key: 'org_id', label: 'Organisation', type: 'select', options: orgs.rows.filter((o) => !o.deleted_at).map((o) => ({ value: o.id, label: o.name })) },
      { key: 'role_title', label: 'Role / title', hint: 'Documents show the role, not the name' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'phone', label: 'Phone' },
      { key: 'whatsapp', label: 'WhatsApp number', hint: 'International format, e.g. +679…' },
      { key: 'notify_email', label: 'Send email notifications', type: 'checkbox' },
      { key: 'notify_whatsapp', label: 'Send WhatsApp messages (needs their opt-in)', type: 'checkbox' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    [orgs.rows],
  );
  const siteFields: FieldSpec[] = useMemo(
    () => [
      {
        key: 'contact_id',
        label: 'Person',
        type: 'select',
        required: true,
        options: contacts.rows.filter((c) => !c.deleted_at).map((c) => ({ value: c.id, label: `${c.full_name} — ${orgName(c.org_id)}` })),
      },
      { key: 'site_role', label: 'Role on this site', type: 'select', required: true, options: SITE_ROLES },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contacts.rows, orgs.rows],
  );

  const contactName = (id: string) => contacts.rows.find((c) => c.id === id);

  return (
    <>
      <PageHeader
        title="Organisations & contacts"
        sub="Shared across all clients/sites. Site roles are per site."
        actions={
          editable ? (
            <Button kind="primary" onClick={() => (tab === 'orgs' ? setOpenOrg('new') : tab === 'contacts' ? setOpenContact('new') : setOpenSite('new'))}>
              + Add {tab === 'orgs' ? 'organisation' : tab === 'contacts' ? 'contact' : 'site role'}
            </Button>
          ) : null
        }
      />
      <div className="toolbar" role="tablist">
        {(
          [
            ['orgs', `Organisations (${orgs.rows.filter((o) => !o.deleted_at).length})`],
            ['contacts', `Contacts (${contacts.rows.filter((c) => !c.deleted_at).length})`],
            ['site', `Roles on ${site?.short_code ?? 'site'}`],
          ] as const
        ).map(([k, l]) => (
          <Button key={k} kind={tab === k ? 'primary' : 'secondary'} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {l}
          </Button>
        ))}
        {tab !== 'site' ? <input type="search" placeholder="Find" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find" /> : null}
      </div>

      {tab === 'orgs' ? (
        <Card pad={false}>
          {orgs.loading ? <Empty>Loading…</Empty> : (
            <div className="table-wrap">
              <table className="t">
                <thead><tr><th>Organisation</th><th>Code</th><th>Type</th><th>Other names</th><th>Contacts</th><th>Notes</th></tr></thead>
                <tbody>
                  {orgs.rows.filter((o) => match([o.name, o.short_code, ...(o.aliases ?? [])].join(' '))).map((o) => (
                    <tr key={o.id} className={`click ${o.deleted_at ? 'deleted' : ''}`} onClick={() => setOpenOrg(o)}>
                      <td><strong>{o.name}</strong></td>
                      <td>{o.short_code ?? '—'}</td>
                      <td><Badge>{o.kind}</Badge></td>
                      <td className="small">{o.aliases?.join(', ') || '—'}</td>
                      <td>{contacts.rows.filter((c) => c.org_id === o.id && !c.deleted_at).length}</td>
                      <td className="small muted">{o.notes ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}

      {tab === 'contacts' ? (
        <Card pad={false}>
          {contacts.loading ? <Empty>Loading…</Empty> : contacts.rows.length === 0 ? <Empty>No contacts yet. Add the people you deal with at each organisation.</Empty> : (
            <div className="table-wrap">
              <table className="t">
                <thead><tr><th>Name</th><th>Organisation</th><th>Role</th><th>Email</th><th>Phone / WhatsApp</th></tr></thead>
                <tbody>
                  {contacts.rows.filter((c) => match([c.full_name, c.email, c.role_title, orgName(c.org_id)].join(' '))).map((c) => (
                    <tr key={c.id} className={`click ${c.deleted_at ? 'deleted' : ''}`} onClick={() => setOpenContact(c)}>
                      <td><strong>{c.full_name}</strong></td>
                      <td>{orgName(c.org_id)}</td>
                      <td>{c.role_title ?? '—'}</td>
                      <td>{c.email ?? '—'}</td>
                      <td>{c.phone ?? '—'}{c.whatsapp ? <div className="muted small">WA {c.whatsapp}</div> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}

      {tab === 'site' ? (
        <Card pad={false}>
          {siteContacts.rows.length === 0 ? <Empty>Nobody has a role on this site yet — e.g. the client finance team or the architect of record.</Empty> : (
            <div className="table-wrap">
              <table className="t">
                <thead><tr><th>Role</th><th>Person</th><th>Organisation</th><th>Email</th></tr></thead>
                <tbody>
                  {siteContacts.rows.map((s) => {
                    const c = contactName(s.contact_id);
                    return (
                      <tr key={s.id} className={`click ${s.deleted_at ? 'deleted' : ''}`} onClick={() => setOpenSite(s)}>
                        <td><Badge tone="dark">{SITE_ROLES.find((r) => r.value === s.site_role)?.label ?? s.site_role}</Badge></td>
                        <td>{c?.full_name ?? '—'}</td>
                        <td>{orgName(c?.org_id ?? null)}</td>
                        <td>{c?.email ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}

      <RecordDrawer open={openOrg !== null} onClose={() => setOpenOrg(null)} title={openOrg === 'new' ? 'Add organisation' : openOrg ? openOrg.name : ''}
        table="organisations" fields={orgFields} record={openOrg === 'new' ? null : openOrg} defaults={{ kind: 'contractor' }} canEdit={editable}
        onSaved={() => void orgs.reload()} label={(r) => `organisation ${String(r.name ?? '')}`} />
      <RecordDrawer open={openContact !== null} onClose={() => setOpenContact(null)} title={openContact === 'new' ? 'Add contact' : openContact ? openContact.full_name : ''}
        table="contacts" fields={contactFields} record={openContact === 'new' ? null : openContact} defaults={{ notify_email: true }} canEdit={editable}
        onSaved={() => void contacts.reload()} label={(r) => `contact ${String(r.full_name ?? '')}`} />
      <RecordDrawer open={openSite !== null} onClose={() => setOpenSite(null)} title={openSite === 'new' ? 'Add role on this site' : 'Role on this site'}
        table="site_contacts" fields={siteFields} record={openSite === 'new' ? null : openSite} defaults={{ site_id: siteId }} canEdit={editable}
        onSaved={() => void siteContacts.reload()} label={(r) => `site role ${String(r.site_role ?? '')}`} />
    </>
  );
}
